"use client";
import { UserStatusCode } from "../services/driver-service";

export interface UserRow {
    id: number;
    username: string;            // mã nhân viên
    name: string;            // tên nhân viên
    email: string;            
    phone: string;
    department_id?: number;    // id bộ phận
    department_name?: string; // tên bộ phận
    birthday?: string;       // ngày sinh
    joining_date?: string;      // ngày vào làm
    privilege_group_id?: number;
    privilege_group_name?: string;
    gender?: number;
    address?: string;
    avatar?: string;
    id_card_number?: string;
    created_at: string;      // ngày tạo
    statusCode: UserStatusCode;
    branch_ids?: number[] 
  }
  

interface UserTableProps {
  rows: UserRow[];
  isLoading: boolean;
  startIndex: number;
  onEdit?: (row: UserRow) => void;
  onDeactivate?: (row: UserRow) => void;
  onActivate?: (row: UserRow) => void;
  onViewDetail?: (row: UserRow) => void;
  onResetPassword?: (row: UserRow) => void;
}
const formatDateToDDMMYYYY = (dateStr?: string | null): string | undefined => {
  if (!dateStr) return undefined;

  // Tách phần ngày và giờ
  const [datePart, timePart] = dateStr.split(" ");
  if (!datePart) return undefined;

  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return undefined;

  // Trả về định dạng: 24-09-2025 09:31:52
  return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}${timePart ? ` ${timePart}` : ""}`;
};

const UserTable: React.FC<UserTableProps> = ({
  rows,
  isLoading,
  startIndex,
  onEdit,
  onDeactivate,
  onActivate,
  onViewDetail,
  onResetPassword,
}) => {
  if (isLoading) return <div className="p-4 text-gray-500">Đang tải...</div>;
  if (!rows.length) return <div className="p-4 text-gray-500">Không có dữ liệu</div>;

  return (
    <table className="min-w-full border text-sm">
    <thead className="bg-gray-100">
        <tr>
        <th className="border px-3 py-2 text-center">STT</th>
        <th className="border px-3 py-2 text-center">Mã nhân viên</th>
        <th className="border px-3 py-2 text-center">Tên nhân viên</th>
        <th className="border px-3 py-2 text-center">Bộ phận</th>
        <th className="border px-3 py-2 text-center">Ngày sinh</th>
        <th className="border px-3 py-2 text-center">Ngày vào làm</th>
        <th className="border px-3 py-2 text-center">Ngày tạo</th>
        <th className="border px-3 py-2 text-center">Thao tác</th>
        </tr>
    </thead>
    <tbody>
        {rows.map((row, idx) => (
        <tr key={row.id} className="hover:bg-gray-50 group">
            <td className="border px-3 py-2 text-center">{startIndex + idx}</td>
            <td className="border px-3 py-2">{row.username}</td>
            <td className="border px-3 py-2">{row.name}</td>
            <td className="border px-3 py-2">{row.department_name || "—"}</td>
            <td className="border px-3 py-2">{row.birthday || "—"}</td>
            <td className="border px-3 py-2">{row.joining_date || "—"}</td>
            <td className="border px-3 py-2">{row.created_at || "—"}</td>
            <td className="border px-3 py-2">
          <div className="flex justify-center gap-2">
            {/* Xem chi tiết */}
            {onViewDetail && (
              <button
                type="button"
                onClick={() => onViewDetail(row)}
                aria-label="Xem chi tiết"
                className="group relative grid h-9 w-9 place-items-center rounded-full bg-slate-500 text-white shadow-sm hover:bg-slate-600"
              >
                {/* eye icon */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                    className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                  Xem
                </span>
              </button>
            )}

            {/* Sửa */}
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(row)}
                aria-label="Sửa"
                className="group relative grid h-9 w-9 place-items-center rounded-full bg-yellow-500 text-white shadow-sm hover:bg-yellow-600"
              >
                {/* pencil icon */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                    className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round"
                        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652l-9.547
                          9.546a4.5 4.5 0 01-1.897 1.13l-3.034.909.91-3.034a4.5
                          4.5 0 011.129-1.897l9.547-9.546z"/>
                </svg>
                <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                  Sửa
                </span>
              </button>
            )}

            {/* Đặt lại mật khẩu */}
            {onResetPassword && (
              <button
                type="button"
                onClick={() => onResetPassword(row)}
                aria-label="Đặt lại mật khẩu"
                className="group relative grid h-9 w-9 place-items-center rounded-full bg-blue-500 text-white shadow-sm hover:bg-blue-600"
              >
                {/* key icon */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                    className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v-2.25l1.159-1.159c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
                <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                  Reset Mật khẩu
                </span>
              </button>
            )}
            
            {/* Ngưng */}
            {row.statusCode === UserStatusCode.ACTIVE && onDeactivate && (
              <button
                type="button"
                onClick={() => onDeactivate(row)}
                aria-label="Ngưng"
                className="group relative grid h-9 w-9 place-items-center rounded-full bg-red-500 text-white shadow-sm hover:bg-red-600"
              >
                {/* stop in circle icon */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                    className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75h4.5v4.5h-4.5z"/>
                  <path strokeLinecap="round" strokeLinejoin="round"
                        d="M12 21.75c5.385 0 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25
                          2.25 6.615 2.25 12 6.615 21.75 12 21.75z"/>
                </svg>
                <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                  Ngưng
                </span>
              </button>
            )}

            {/* Kích hoạt */}
            {row.statusCode === UserStatusCode.INACTIVE && onActivate && (
              <button
                type="button"
                onClick={() => onActivate(row)}
                aria-label="Kích hoạt"
                className="group relative grid h-9 w-9 place-items-center rounded-full bg-green-500 text-white shadow-sm hover:bg-green-600"
              >
                {/* check icon */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                    className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                </svg>
                <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                  Kích hoạt
                </span>
              </button>
            )}
          </div>
        </td>
        </tr>
        ))}
    </tbody>
    </table>
  );
};

export default UserTable;
